import { Rate, Segmented, Table } from 'antd'
import { useMemo, useState, type Key } from 'react'
import { MdOutlineStarOutline, MdStar } from 'react-icons/md'

import { cx } from '@/Ship/Helpers/cssClass'
import { useLayoutHeaderHeight } from '@/Ship/Hooks/useLayoutHeaderHeight'
import { pluralizeUk } from '@/Ship/Helpers/pluralize'

import { GroupName } from '../Components/GroupName'
import { RatingDistributionBar } from '../Components/RatingDistributionBar'
import { RatingItems } from '../Components/RatingItems'
import { ReportExportButton } from '../Components/ReportExportButton'
import { ReportFilters } from '../Components/ReportFilters'
import { ReportShell } from '../Components/ReportShell'
import { ReportSummary, type ReportKpi } from '../Components/ReportSummary'
import { ReportEmpty, ReportError } from '../Components/ReportTableState'
import type { RatingGroupBy, RatingReport, RatingRow, ReportFilterState } from '../Contracts/Report'
import { GROUP_TITLES, groupKeyParam, groupLabel, RATING_GROUPS } from '../Helpers/reportGroups'
import { taskReportShellChips } from '../Helpers/reportChips'
import { formatPeriodLabel } from '../Helpers/reportPeriod'
import { buildReportParams, initialReportFilters } from '../Helpers/reportQuery'
import { useReportFetch } from '../Hooks/useReportFetch'
import { useTableSort } from '../Hooks/useTableSort'

import styles from '../Components/Report.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

import '@/Ship/Templates/CrudResource/Table.css'

/** Колонки таблиці з сортуванням (ключі — як `sort` в експорті) */
type RatingSortKey = 'group' | 'averageRating' | 'ratingsCount' | 'tasks'

/**
 * Звіт «Рейтинг диспетчера»: оцінки водіїв, поставлені при завершенні задач, у розрізі
 * відповідального диспетчера або водія — середня, кількість, розподіл 1–5, задачі.
 * Рядок розгортається в самі оцінки з коментарями.
 */
const DispatcherRatingPage = () => {
    const [filters, setFilters] = useState<ReportFilterState>(initialReportFilters)
    const [groupBy, setGroupBy] = useState<RatingGroupBy>('dispatcher')
    const [expandedKeys, setExpandedKeys] = useState<Key[]>([])
    // Сортування кероване: експорт віддає рядки в тому ж порядку, що й таблиця
    const { sortOrder, onChange: onSortChange, sortParams } = useTableSort<RatingSortKey, RatingRow>()
    const headerHeight = useLayoutHeaderHeight()

    const params = useMemo(() => buildReportParams(filters), [filters])
    const { data, dataParams, loading, error, reload } = useReportFetch<RatingReport>(
        '/services/reports/dispatcher-rating',
        { ...params, groupBy }
    )

    // Інші фільтри чи розріз — інші групи: розгорнуті рядки згортаємо одразу (не в ефекті),
    // щоб деталізація не встигла запросити старий ключ з новим розрізом
    const changeFilters = (next: ReportFilterState) => {
        setExpandedKeys([])
        setFilters(next)
    }
    const changeGroupBy = (next: RatingGroupBy) => {
        setExpandedKeys([])
        setGroupBy(next)
    }
    const rows = data && dataParams?.['groupBy'] === groupBy ? data.items : []

    const totals = data?.totals
    const average = totals?.averageRating ?? null
    const kpis: ReportKpi[] = [
        {
            key: 'average',
            label: 'Середня оцінка',
            value:
                average !== null ? (
                    <>
                        <MdStar className={cx(styles, 'star')} aria-hidden="true" />
                        {average.toFixed(1)}
                    </>
                ) : (
                    '—'
                ),
            hint: 'з 5',
        },
        {
            key: 'count',
            label: 'Оцінок',
            value: totals?.ratingsCount ?? 0,
            hint: `за ${totals?.tasks ?? 0} ${pluralizeUk(totals?.tasks ?? 0, ['задачу', 'задачі', 'задач'])}`,
        },
        {
            key: 'distribution',
            label: 'Розподіл оцінок',
            plain: true,
            wide: true,
            value: totals ? <RatingDistributionBar distribution={totals.distribution} withLegend /> : null,
        },
    ]

    const columns = [
        {
            title: GROUP_TITLES[groupBy],
            key: 'group',
            sortOrder: sortOrder('group'),
            sorter: (a: RatingRow, b: RatingRow) =>
                (groupLabel(groupBy, a) ?? '').localeCompare(groupLabel(groupBy, b) ?? '', 'uk'),
            render: (_: unknown, row: RatingRow) => <GroupName groupBy={groupBy} row={row} />,
        },
        {
            title: 'Середня',
            dataIndex: 'averageRating',
            key: 'averageRating',
            width: 190,
            sortOrder: sortOrder('averageRating'),
            sorter: (a: RatingRow, b: RatingRow) => (a.averageRating ?? -1) - (b.averageRating ?? -1),
            render: (v: number | null) =>
                v === null ? (
                    <span className={cx(styles, 'faint')}>—</span>
                ) : (
                    <span className={cx(styles, 'rating')}>
                        <Rate disabled allowHalf value={Math.round(v * 2) / 2} />
                        <span className={cx(styles, 'num')}>{v.toFixed(1)}</span>
                    </span>
                ),
        },
        {
            title: 'Оцінок',
            dataIndex: 'ratingsCount',
            key: 'ratingsCount',
            width: 100,
            align: 'right' as const,
            sortOrder: sortOrder('ratingsCount'),
            sorter: (a: RatingRow, b: RatingRow) => a.ratingsCount - b.ratingsCount,
            render: (v: number) => <span className={cx(styles, 'num')}>{v}</span>,
        },
        {
            title: 'Розподіл',
            key: 'distribution',
            width: 200,
            render: (_: unknown, row: RatingRow) => <RatingDistributionBar distribution={row.distribution} />,
        },
        {
            title: 'Задач (підтверджено)',
            dataIndex: 'tasks',
            key: 'tasks',
            width: 170,
            align: 'right' as const,
            sortOrder: sortOrder('tasks'),
            sorter: (a: RatingRow, b: RatingRow) => a.tasks - b.tasks,
            render: (v: number) => <span className={cx(styles, 'num')}>{v}</span>,
        },
    ]

    const hasFilters = Object.keys(params).some((k) => !['from', 'to'].includes(k))
    const subtitle = totals
        ? `${formatPeriodLabel(filters.period)} · ${totals.ratingsCount} ${pluralizeUk(totals.ratingsCount, ['оцінка', 'оцінки', 'оцінок'])}`
        : formatPeriodLabel(filters.period)

    return (
        <ReportShell
            title="Рейтинг диспетчера"
            subtitle={subtitle}
            filterPanel={<ReportFilters value={filters} onChange={changeFilters} />}
            chips={taskReportShellChips(filters, changeFilters)}
            onClearFilters={() => changeFilters(initialReportFilters())}
            onRefresh={reload}
            refreshing={loading}
            storageKey="autostrada.report.rating.filterOpen"
            actions={
                <ReportExportButton
                    url="/services/reports/dispatcher-rating/export"
                    params={{ ...params, groupBy, ...sortParams }}
                    fallbackFileName="Рейтинг диспетчера.xlsx"
                />
            }
        >
            <ReportSummary items={kpis} loading={loading && !data} />

            <div className={cx(styles, 'toolbar')}>
                <div className={cx(styles, 'toolbarGroup')}>
                    <span className={cx(styles, 'toolbarLabel')}>Рейтинг</span>
                    <Segmented<RatingGroupBy> options={RATING_GROUPS} value={groupBy} onChange={changeGroupBy} />
                </div>
                <span className={cx(styles, 'toolbarHint')}>Клік по рядку розгортає оцінки з коментарями</span>
            </div>

            {error ? (
                <ReportError message={error} onRetry={reload} />
            ) : (
                <Table<RatingRow>
                    className={`wl-table ${cx(styles, 'table')}`}
                    size="middle"
                    rowKey={groupKeyParam}
                    loading={loading}
                    columns={columns}
                    dataSource={rows}
                    onChange={onSortChange}
                    pagination={{
                        pageSize: 50,
                        showSizeChanger: false,
                        showTotal: (total, range) => (total > 0 ? `${range[0]}–${range[1]} з ${total}` : ''),
                    }}
                    scroll={{ x: 'max-content' }}
                    sticky={{ offsetHeader: headerHeight }}
                    locale={{
                        emptyText: loading ? (
                            ' '
                        ) : (
                            <ReportEmpty hasFilters={hasFilters} allTime={filters.period === null} />
                        ),
                    }}
                    rowClassName={(row) => (row.ratingsCount > 0 ? 'wl-table-row-clickable' : '')}
                    expandable={{
                        expandedRowKeys: expandedKeys,
                        onExpandedRowsChange: (keys) => setExpandedKeys([...keys]),
                        expandRowByClick: true,
                        rowExpandable: (row) => row.ratingsCount > 0,
                        expandedRowRender: (row) => (
                            <RatingItems groupBy={groupBy} groupKey={groupKeyParam(row)} params={params} />
                        ),
                    }}
                />
            )}
        </ReportShell>
    )
}

export default DispatcherRatingPage

export const pageConfig: IPageConfig = {
    route: '/reports/dispatcher-rating',
    key: 'Report.DispatcherRating',
    title: 'Рейтинг диспетчера',
    icon: <MdOutlineStarOutline />,
}
